import type { PackageContent } from 'shared/packages/domain/packageContent';
import { PackageComposition } from 'shared/packages/domain/packageComposition';

export interface DerivedMeasurements {
  volume: number | null;
  runningMeters: number | null;
}

// Mirrors the backend calculation: dimensions in mm, volume in m³, running meters in m.
export function computeDerivedMeasurements(
  pieces: number | null,
  thickness: number | null,
  width: number | null,
  length: number | null,
): DerivedMeasurements {
  if (pieces === null) {
    return { volume: null, runningMeters: null };
  }

  const volume = thickness !== null && width !== null && length !== null
    ? pieces * (thickness / 1000) * (width / 1000) * (length / 1000)
    : null;
  const runningMeters = length !== null ? pieces * (length / 1000) : null;

  return { volume, runningMeters };
}

export function contentMeasurements(content: PackageContent): { physical: DerivedMeasurements; nominal: DerivedMeasurements } {
  return {
    physical: computeDerivedMeasurements(content.pieces, content.thickness, content.width, content.length),
    nominal: computeDerivedMeasurements(content.pieces, content.nominalThickness, content.nominalWidth, content.nominalLength),
  };
}

export function applyContentMeasurements(content: PackageContent): void {
  const { physical, nominal } = contentMeasurements(content);
  if (physical.volume !== null) content.volume = physical.volume;
  if (physical.runningMeters !== null) content.runningMeters = physical.runningMeters;
  if (nominal.volume !== null) content.nominalVolume = nominal.volume;
  if (nominal.runningMeters !== null) content.nominalRunningMeters = nominal.runningMeters;
}

type SummableField = 'volume' | 'runningMeters' | 'nominalVolume' | 'nominalRunningMeters';

/** Sum of the field over all contents, or null when no content has a value. */
export function sumContents(contents: PackageContent[], field: SummableField): number | null {
  const values = contents.map((content) => content[field]).filter((value): value is number => value !== null);
  return values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0);
}

export function packageComposition(contents: PackageContent[]): PackageComposition {
  const products = new Set(contents.map((content) => content.productId).filter((id): id is string => !!id));
  if (products.size === 0) return PackageComposition.Empty;
  return products.size === 1 ? PackageComposition.Uniform : PackageComposition.Mixed;
}

export interface Layering {
  layers: number | null;
  piecesPerLayer: number | null;
  spacerHeight: number | null;
}

export interface PackageDimensions {
  height: number | null;
  width: number | null;
  length: number | null;
}

function maxOf(contents: PackageContent[], field: 'thickness' | 'width' | 'length' | 'nominalThickness' | 'nominalWidth' | 'nominalLength'): number | null {
  const values = contents.map((content) => content[field]).filter((value): value is number => value !== null);
  return values.length === 0 ? null : Math.max(...values);
}

/** Total package dimensions (mm) of a uniform package; only meaningful for a single product. */
export function uniformPackageDimensions(contents: PackageContent[], layering: Layering, kind: 'physical' | 'nominal'): PackageDimensions {
  const nominal = kind === 'nominal';
  const thickness = maxOf(contents, nominal ? 'nominalThickness' : 'thickness');
  const width = maxOf(contents, nominal ? 'nominalWidth' : 'width');
  const { layers, piecesPerLayer, spacerHeight } = layering;

  return {
    height: layers !== null && thickness !== null
      ? layers * thickness + Math.max(layers - 1, 0) * (spacerHeight ?? 0)
      : null,
    width: piecesPerLayer !== null && width !== null ? piecesPerLayer * width : null,
    length: maxOf(contents, nominal ? 'nominalLength' : 'length'),
  };
}

/** Package volume (m³) from its total dimensions; only used when the package has no contents. */
export function packageVolume(height: number | null, width: number | null, length: number | null): number | null {
  return height !== null && width !== null && length !== null
    ? (height / 1000) * (width / 1000) * (length / 1000)
    : null;
}

/** Package running meters (m); only used when the package has no contents. */
export function packageRunningMeters(piecesCount: number | null, length: number | null): number | null {
  return piecesCount !== null && length !== null ? piecesCount * (length / 1000) : null;
}
